import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, User, Mail, Shield, LogOut, Key, Crown, Download, Share, Smartphone, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PasswordInput } from '../components/PasswordInput';
import { useInstallApp } from '../hooks/useInstallApp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Account({ user }) {
  const navigate = useNavigate();
  const { canPrompt, isInstalled, platform, triggerInstall } = useInstallApp();
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Host application state
  const [hostAppStatus, setHostAppStatus] = useState(null);
  const [showHostApp, setShowHostApp] = useState(false);
  const [hostAppReason, setHostAppReason] = useState('');
  const [submittingHostApp, setSubmittingHostApp] = useState(false);

  const isRegularUser = user?.role === 'user';

  useEffect(() => {
    if (isRegularUser) {
      const token = localStorage.getItem('session_token');
      axios.get(`${API}/users/host-application-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setHostAppStatus(r.data))
        .catch(() => {});
    }
  }, [isRegularUser]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmitHostApp = async (e) => {
    e.preventDefault();
    setSubmittingHostApp(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/users/apply-host`, { reason: hostAppReason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Application submitted!');
      setShowHostApp(false);
      setHostAppStatus({ status: 'pending' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmittingHostApp(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button onClick={() => {
            const stored = localStorage.getItem('user');
            const role = stored ? (JSON.parse(stored).role || 'user') : 'user';
            navigate(role === 'host' || role === 'admin' ? '/dashboard' : '/my-tickets');
          }} variant="ghost" className="mb-4 -ml-2 text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">My Account</h1>
          <p className="mt-1 text-base text-[#AFAFAF]">Manage your profile and security</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        {/* Profile Info */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="profile-section">
          <h2 className="text-lg font-semibold text-[#EAE7E1] mb-4 tracking-tight">Profile</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-4 p-4 bg-[#050505] border border-[#1A1A1A] rounded-sm">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <User className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold text-[#EAE7E1] truncate" data-testid="user-name">{user?.name || 'User'}</div>
                <div className="flex items-center text-sm text-[#AFAFAF]">
                  <Mail className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                  <span className="truncate" data-testid="user-email">{user?.email}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <Shield className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#AFAFAF]">Role:</span>
              <span className="px-2 py-0.5 text-xs font-bold uppercase rounded bg-[#D4AF37]/20 text-[#D4AF37]" data-testid="user-role">{user?.role}</span>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="password-section">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-[#EAE7E1] tracking-tight">Security</h2>
            {!showPasswordForm && (
              <Button onClick={() => setShowPasswordForm(true)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="change-password-toggle">
                <Key className="w-4 h-4 mr-2" /> Change Password
              </Button>
            )}
          </div>
          {showPasswordForm ? (
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
              <div>
                <Label>Current Password</Label>
                <PasswordInput value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required className="mt-1" data-testid="current-password-input" />
              </div>
              <div>
                <Label>New Password</Label>
                <PasswordInput value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required minLength={6} className="mt-1" data-testid="new-password-input" />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <PasswordInput value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required className="mt-1" data-testid="confirm-password-input" />
              </div>
              <div className="flex space-x-3">
                <Button type="button" onClick={() => { setShowPasswordForm(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }} variant="outline" className="flex-1 border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="cancel-password-button">Cancel</Button>
                <Button type="submit" disabled={changingPassword} className="flex-1 bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="save-password-button">
                  {changingPassword ? 'Saving...' : 'Update Password'}
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-[#AFAFAF]">Change your account password to keep your account secure.</p>
          )}
        </div>

        {/* Host Application — only for regular users */}
        {isRegularUser && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="host-application-section">
            <div className="flex items-center gap-3 mb-3">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-semibold text-[#EAE7E1] tracking-tight">Become a Host</h2>
            </div>

            {hostAppStatus?.status === 'pending' ? (
              <div data-testid="host-app-pending">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">Pending Review</span>
                </div>
                <p className="text-sm text-[#AFAFAF]">Your host application is under review. You'll be notified once it's approved.</p>
              </div>
            ) : hostAppStatus?.status === 'approved' ? (
              <div data-testid="host-app-approved">
                <p className="text-sm text-emerald-400">Your application has been approved! Log out and log back in to access host features.</p>
              </div>
            ) : hostAppStatus?.status === 'rejected' ? (
              <div data-testid="host-app-rejected">
                <p className="text-sm text-[#AFAFAF]">Your previous application was not approved. You can reapply if circumstances have changed.</p>
                <Button onClick={() => setShowHostApp(true)} variant="outline" className="mt-3 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid="reapply-host-button">Reapply</Button>
              </div>
            ) : !showHostApp ? (
              <div>
                <p className="text-sm text-[#AFAFAF] mb-2">Want to create and manage your own events? Apply for host access.</p>
                <p className="text-xs text-[#777] mb-3">Applications are reviewed by the VelvetGate team. You'll be notified once approved. Staff check-in roles are invited by an existing host — no application needed.</p>
                <Button onClick={() => setShowHostApp(true)} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid="apply-host-button">
                  <Crown className="w-4 h-4 mr-2" />Apply to Host
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmitHostApp} className="space-y-3" data-testid="host-application-form">
                <div>
                  <Label className="text-[#AFAFAF] text-sm">Why do you want to become a host? (optional)</Label>
                  <textarea
                    value={hostAppReason}
                    onChange={e => setHostAppReason(e.target.value)}
                    placeholder="Tell us about the events you'd like to create..."
                    rows={3}
                    className="w-full mt-1 px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-[#EAE7E1] placeholder:text-[#555] focus:border-[#D4AF37] resize-none text-sm"
                    data-testid="host-app-reason"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={submittingHostApp} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="submit-host-app">
                    {submittingHostApp ? 'Submitting...' : 'Submit Application'}
                  </Button>
                  <Button type="button" onClick={() => setShowHostApp(false)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm">Cancel</Button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Install App */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="install-app-section">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-semibold text-[#EAE7E1] tracking-tight">Install App</h2>
          </div>
          {isInstalled ? (
            <div className="flex items-center gap-2 text-sm text-emerald-400" data-testid="install-already-installed">
              <Check className="w-4 h-4" />
              <span>VelvetGate is installed on this device</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#AFAFAF] mb-4">Install VelvetGate for faster access — opens like a real app, no browser bar.</p>
              {canPrompt ? (
                <Button onClick={async () => { const ok = await triggerInstall(); if (ok) toast.success('VelvetGate installed!'); }} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="install-app-button">
                  <Download className="w-4 h-4 mr-2" />Install VelvetGate
                </Button>
              ) : (
                <>
                  <Button onClick={() => setShowIOSGuide(!showIOSGuide)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="install-app-button">
                    <Download className="w-4 h-4 mr-2" />Install VelvetGate
                  </Button>
                  {showIOSGuide && (
                    <div className="mt-4 p-4 bg-[#050505] border border-[#1A1A1A] rounded-sm space-y-3" data-testid="install-guide">
                      <p className="text-sm font-medium text-[#EAE7E1]">
                        {platform === 'ios' ? 'How to install on iPhone:' : platform === 'android' ? 'How to install on Android:' : 'How to install:'}
                      </p>
                      {platform === 'ios' ? (
                        <div className="space-y-2">
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">1</span>
                            <p className="text-sm text-[#AFAFAF]">Tap the <Share className="w-4 h-4 inline text-[#D4AF37]" /> <strong className="text-[#EAE7E1]">Share</strong> button at the bottom of Safari</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">2</span>
                            <p className="text-sm text-[#AFAFAF]">Tap <strong className="text-[#EAE7E1]">"Add to Home Screen"</strong></p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">3</span>
                            <p className="text-sm text-[#AFAFAF]">Tap <strong className="text-[#EAE7E1]">"Add"</strong> to confirm</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">1</span>
                            <p className="text-sm text-[#AFAFAF]">Tap the <strong className="text-[#EAE7E1]">browser menu</strong> (three dots, top right)</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">2</span>
                            <p className="text-sm text-[#AFAFAF]">Tap <strong className="text-[#EAE7E1]">"Add to Home screen"</strong> or <strong className="text-[#EAE7E1]">"Install app"</strong></p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">3</span>
                            <p className="text-sm text-[#AFAFAF]">Tap <strong className="text-[#EAE7E1]">"Install"</strong> or <strong className="text-[#EAE7E1]">"Add"</strong> to confirm</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Logout */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="logout-section">
          <h2 className="text-lg font-semibold text-[#EAE7E1] mb-2 tracking-tight">Session</h2>
          <p className="text-sm text-[#AFAFAF] mb-4">Sign out of your VelvetGate account on this device.</p>
          <Button onClick={handleLogout} variant="outline" className="border-[#C44545]/30 text-[#C44545] hover:bg-[#C44545]/10 rounded-sm" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" /> Log Out
          </Button>
        </div>
      </div>
    </div>
  );
}
